import React, { useState } from 'react';
import { Container, Row, Col, Card, Accordion, Form, Button } from 'react-bootstrap';

const FAQ = () => {
  const [searchTerm, setSearchTerm] = useState('');

  const faqs = [
    {
      category: 'General',
      questions: [
        {
          question: 'What is WiSE (Women SHG Enterprises)?',
          answer: 'WiSE is a dairy entrepreneurship initiative that empowers rural women through Self Help Groups (SHGs). We provide training, technology, market linkages, and fair pricing systems to help women build sustainable dairy businesses and improve their livelihoods.'
        },
        {
          question: 'How does WiSE support women entrepreneurs?',
          answer: 'We provide comprehensive support including: training programs on dairy management and entrepreneurship, IoT technology for quality testing and fair pricing, buy-back guarantee for products, market linkages, financial guidance, and ongoing technical support.'
        },
        {
          question: 'What makes WiSE different from other dairy initiatives?',
          answer: 'WiSE combines traditional dairy practices with modern IoT technology for quality testing and fair pricing. Our focus on women empowerment through SHGs, comprehensive training programs, and guaranteed buy-back makes us unique in the dairy sector.'
        }
      ]
    },
    {
      category: 'SHG Membership',
      questions: [
        {
          question: 'How can SHGs join WiSE?',
          answer: 'SHGs can join WiSE by contacting us through our website, phone, or visiting our offices. We conduct an initial assessment of the group\'s capabilities and provide guidance on the joining process. Training and support begin immediately after registration.'
        },
        {
          question: 'What are the requirements to join as an SHG member?',
          answer: 'Basic requirements include: being part of a registered SHG, having access to dairy animals (cows/buffaloes), commitment to quality milk production, willingness to adopt new technologies and practices, and participation in training programs.'
        },
        {
          question: 'Is there any fee to join WiSE?',
          answer: 'There is no joining fee for SHGs. WiSE provides free training, technical support, and access to our technology platform. We invest in our members\' success as our growth depends on their prosperity.'
        },
        {
          question: 'How many SHG members are currently part of WiSE?',
          answer: 'Currently, we have 65+ active SHG members across 5 villages in Tamil Nadu. Our member base has grown from just 10 women when we started, and we continue to expand our reach to more villages.'
        }
      ]
    },
    {
      category: 'Products & Services',
      questions: [
        {
          question: 'What products does WiSE offer?',
          answer: 'We offer fresh cow and buffalo milk, paneer, butter, pure ghee, flavored milk, curd, yogurt, and khoa. We are also developing fortified yogurt, millet ice cream, and functional beverages for future launch.'
        },
        {
          question: 'Do you provide doorstep delivery?',
          answer: 'Yes, we provide doorstep delivery for our dairy products in our service areas. Delivery schedules depend on location and product availability. Contact us to check delivery availability in your area.'
        },
        {
          question: 'How do you ensure product quality?',
          answer: 'We use IoT milk analyzer technology for real-time quality testing. All products undergo rigorous quality checks, and we maintain cold chain throughout the process. Our products are 100% natural with no artificial preservatives or additives.'
        },
        {
          question: 'Are your products certified?',
          answer: 'Yes, all our products meet government quality standards and food safety regulations. We are recognized by the Tamil Nadu government and work closely with TANUVAS for quality assurance.'
        }
      ]
    },
    {
      category: 'Technology & Pricing',
      questions: [
        {
          question: 'How does the IoT milk analyzer work?',
          answer: 'Our IoT milk analyzer provides real-time analysis of milk composition including fat content, protein levels, and purity. This ensures accurate quality assessment and enables fair pricing based on actual milk quality rather than arbitrary rates.'
        },
        {
          question: 'How is milk pricing determined?',
          answer: 'Milk pricing is based on quality parameters measured by our IoT analyzer. Higher quality milk (better fat and protein content) gets better pricing. This ensures our farmers are rewarded fairly for producing quality milk.'
        },
        {
          question: 'What is the buy-back guarantee?',
          answer: 'We guarantee to purchase all quality milk produced by our SHG members at fair market rates. This provides income security and eliminates the worry of finding buyers for their produce.'
        }
      ]
    },
    {
      category: 'Training & Support',
      questions: [
        {
          question: 'What training programs do you offer?',
          answer: 'We offer training on: dairy farming best practices, animal health and nutrition, milk quality improvement, value-added product development, business management skills, financial literacy, and technology adoption.'
        },
        {
          question: 'How often are training sessions conducted?',
          answer: 'We conduct regular monthly training sessions, quarterly advanced workshops, and special programs as needed. We also provide on-demand support and guidance to our members.'
        },
        {
          question: 'Is training provided in local language?',
          answer: 'Yes, all our training programs are conducted in Tamil, the local language, to ensure better understanding and effective learning for our SHG members.'
        }
      ]
    },
    {
      category: 'Partnerships',
      questions: [
        {
          question: 'How can NGOs partner with WiSE?',
          answer: 'NGOs can partner with us to expand our reach, provide additional support services, or collaborate on specific programs. We welcome partnerships that align with our mission of women empowerment and rural development.'
        },
        {
          question: 'Do you collaborate with government agencies?',
          answer: 'Yes, we actively collaborate with government agencies, TANUVAS, CFDT, and other institutions. We participate in government schemes and programs that benefit our SHG members and support policy development.'
        },
        {
          question: 'Can businesses partner with WiSE for CSR activities?',
          answer: 'Absolutely! We welcome corporate partnerships for CSR activities. Businesses can support our training programs, infrastructure development, or sponsor technology upgrades for SHG members.'
        }
      ]
    }
  ];

  const filteredFAQs = faqs.map(category => ({
    ...category,
    questions: category.questions.filter(
      qa => 
        qa.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
        qa.answer.toLowerCase().includes(searchTerm.toLowerCase())
    )
  })).filter(category => category.questions.length > 0);

  const quickLinks = [
    { title: 'Join as SHG', link: '/contact', icon: '👥' },
    { title: 'Product Catalog', link: '/products', icon: '🥛' },
    { title: 'Training Programs', link: '/services', icon: '🎓' },
    { title: 'Contact Support', link: '/contact', icon: '📞' }
  ];

  return (
    <div className="fade-in">
      {/* Hero Section */}
      <section className="py-5 text-white hero_sec" style={{ background: 'linear-gradient(135deg, #2E8B57, #4682B4)' }}>
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h1 className="display-4 fw-bold mb-3">Frequently Asked Questions</h1>
              <p className="fs-5">Find answers to common questions about WiSE and our services</p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Search Section */}
      <section className="py-4" style={{ backgroundColor: '#f8f9fa' }}>
        <Container>
          <Row>
            <Col lg={6} className="mx-auto">
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Search for questions..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="p-3 border-0 shadow-sm"
                  style={{ borderRadius: '25px' }}
                />
              </Form.Group>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Quick Links */}
      <section className="py-4">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto">
              <h5 className="fw-bold mb-3 text-center">Quick Links</h5>
              <Row>
                {quickLinks.map((link, index) => (
                  <Col md={3} key={index} className="mb-3">
                    <Card className="border-0 shadow-sm hover-lift h-100 text-center">
                      <Card.Body className="p-3">
                        <div style={{ fontSize: '2rem' }}>{link.icon}</div>
                        <h6 className="fw-bold small">{link.title}</h6>
                      </Card.Body>
                    </Card>
                  </Col>
                ))}
              </Row>
            </Col>
          </Row>
        </Container>
      </section>

      {/* FAQ Accordion */}
      <section className="section-padding">
        <Container>
          <Row>
            <Col lg={10} className="mx-auto">
              {filteredFAQs.length > 0 ? (
                filteredFAQs.map((category, categoryIndex) => (
                  <div key={categoryIndex} className="mb-5">
                    <h3 className="fw-bold text-primary mb-4 d-flex align-items-center">
                      <span className="me-2">📋</span>
                      {category.category}
                    </h3>
                    <Accordion>
                      {category.questions.map((qa, qaIndex) => (
                        <Accordion.Item
                          key={`${categoryIndex}-${qaIndex}`}
                          eventKey={`${categoryIndex}-${qaIndex}`}
                          className="border-0 shadow-sm mb-3"
                        >
                          <Accordion.Header className="fw-semibold">
                            {qa.question}
                          </Accordion.Header>
                          <Accordion.Body className="text-muted">
                            {qa.answer}
                          </Accordion.Body>
                        </Accordion.Item>
                      ))}
                    </Accordion>
                  </div>
                ))
              ) : (
                <div className="text-center py-5">
                  <div style={{ fontSize: '3rem' }}>🔍</div>
                  <h4>No questions found</h4>
                  <p className="text-muted">Try different search terms or browse all questions above</p>
                </div>
              )}
            </Col>
          </Row>
        </Container>
      </section>

      {/* Still have questions */}
      <section className="py-5" style={{ backgroundColor: '#f8f9fa' }}>
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center">
              <h2 className="fw-bold mb-3">Still have questions?</h2>
              <p className="text-muted mb-4">
                Can't find what you're looking for? Our team is here to help you with any specific questions or concerns.
              </p>
              <div className="d-flex justify-content-center gap-3">
                <Button className="btn-primary-custom px-4">
                  Contact Support
                </Button>
                <Button variant="outline-primary" className="px-4">
                  Schedule Call
                </Button>
              </div>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Contact Methods */}
      <section className="section-padding">
        <Container>
          <Row>
            <Col lg={8} className="mx-auto text-center mb-4">
              <h2 className="fw-bold mb-3">Get in Touch</h2>
              <p className="text-muted">Multiple ways to reach us for support</p>
            </Col>
          </Row>
          <Row>
            <Col md={4} className="text-center mb-4">
              <Card className="border-0 shadow-sm hover-lift">
                <Card.Body className="p-4">
                  <div style={{ fontSize: '2.5rem' }}>📧</div>
                  <h5 className="fw-bold mt-3">Email Support</h5>
                  <p className="text-muted">cbs@womenshgenterprises.com</p>
                  <small className="text-muted">Response within 24 hours</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="text-center mb-4">
              <Card className="border-0 shadow-sm hover-lift">
                <Card.Body className="p-4">
                  <div style={{ fontSize: '2.5rem' }}>📞</div>
                  <h5 className="fw-bold mt-3">Phone Support</h5>
                  <p className="text-muted">+91-XXXXXXXXX</p>
                  <small className="text-muted">Mon-Sat: 9 AM - 6 PM</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={4} className="text-center mb-4">
              <Card className="border-0 shadow-sm hover-lift">
                <Card.Body className="p-4">
                  <div style={{ fontSize: '2.5rem' }}>🏢</div>
                  <h5 className="fw-bold mt-3">Visit Office</h5>
                  <p className="text-muted">Chennai, Tamil Nadu</p>
                  <small className="text-muted">By appointment</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        </Container>
      </section>
    </div>
  );
};

export default FAQ;